import { useSelector } from 'react-redux';
import { RootState } from '~/store/store.ts';
import { useRef } from 'react';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { errorMessages } from '~/constants/errorMessages.js';
import { resetIITLib } from '~/libs/iit/lib.js';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { setPersonalDataApplicationsError } from '~/store/processing-personal/slices.ts';
import { TDiiaInitializeResponse } from '~/store/diia/types.ts';
import { SerializedError } from '@reduxjs/toolkit';
import { setDiiaSignError } from '~/store/diia/slices.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import {
  initializeDiiaSignatureProcessingPersonal,
  getStatusDiiaSignatureProcessingPersonal,
  getFinalStatusDiiaSignatureProcessingPersonal,
} from '~/store/diia/thunks/signatureProcessingPersonalThunks.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { validateDocumentProcessingPersonal } from '~/store/processing-personal/thunks/validateDocumentProcessingPersonalThunks.ts';
import { TIITSign } from '~/store/iit/types.ts';
import { saveSigningControllerFromIIT, startIITSigningController } from '~/store/iit/thunks.ts';
import { setIITError } from '~/store/iit/slices.ts';
import { SignatureError } from '~/modules/processing-personal/PersonalDataApplicationForm/SignatureError.ts';
import { previewDocumentSubmit } from '~/store/processing-personal/thunks/documentPreviewPersonalThunks.ts';
import { infoMessages } from '~/constants/infoMessages.ts';
import { TDocumentApplication } from '~/modules/processing-personal/PersonalDataApplicationForm/types.ts';
import { selectTokenData } from '~/store/users/selectors';

interface IApplicationSignatureProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  onSignatureSuccess: (successMessage: string) => void;
  onSignatureError: (errorMessage: string, errorCode?: string) => void;
  onQrTimeout: () => void;
}

export const ApplicationSignature = ({
  isModalOpen,
  handleCloseModal,
  onSignatureSuccess,
  onQrTimeout,
}: IApplicationSignatureProps) => {
  const token = useSelector((state: RootState) => state.users.token);
  const { applicationId } = useSelector(selectTokenData) || {};
  const dispatch = useAppDispatch();

  const {
    deepLink,
    loading: isDiiaLoading,
    error: diiaError,
  } = useSelector((state: RootState) => state.diia) || {};

  const {
    applicationDocumentId,
    loading: isProcessingPersonalLoading,
    error: processingPersonalError,
  } = useSelector((state: RootState) => state.processingPersonal) || {};

  const { isLoading: IITLoading, error: IITError } =
    useSelector((state: RootState) => state.iit) || {};

  const timerRef = useRef<{ resetTimer: () => void; startTimer: () => void }>(null);
  const ignoreErrorRef = useRef<boolean>(false);

  const isLoading = isDiiaLoading || isProcessingPersonalLoading || IITLoading;
  const error = diiaError || processingPersonalError || IITError;

  const closeAlertModal = () => {
    diiaError && dispatch(setDiiaSignError(''));
    processingPersonalError && dispatch(setPersonalDataApplicationsError(''));
    IITError && dispatch(setIITError(''));
  };

  const documents = [
    {
      id: applicationDocumentId,
      idDictWebDocument: applicationDocumentId,
      idDoc: applicationDocumentId,
      nameDoc: 'Згода на обробку персональних даних',
    },
  ] as TDocumentApplication[];

  const closeModal = () => {
    handleCloseModal();
    diiaError && dispatch(setDiiaSignError(''));
    processingPersonalError && dispatch(setPersonalDataApplicationsError(''));
    IITError && dispatch(setIITError(''));
  };

  const validateDocument = async () => {
    return dispatch(validateDocumentProcessingPersonal({ applicationId }))
      .unwrap()
      .then((response) => {
        const invalidSignatures = response.documentsWithInvalidSignatures || [];
        const rnokppMismatches = response.documentsWithRNOCPPMismatch || [];
        const generatedProtocols = response.generatedProtocols || [];
        const noKepDocuments = response.noKepDocuments || [];

        if (noKepDocuments.length > 0) {
          dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
          return;
        }

        if (invalidSignatures.length > 0 || rnokppMismatches.length > 0) {
          dispatch(setPersonalDataApplicationsError(errorMessages.NO_KEP_FILE_FOUND));
          return;
        }

        if (generatedProtocols.length > 0) {
          handleSignatureSuccess(infoMessages.DOCUMENTS_SENT);
          return;
        }
      })
      .catch(() => {
        dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
      });
  };

  const handleSignatureDiia = async () => {
    if (isLoading) return;
    try {
      const { payload } = await dispatch(
        initializeDiiaSignatureProcessingPersonal({
          applicationId,
          token,
        })
      );
      const requestId = (payload as TDiiaInitializeResponse)?.requestId;
      timerRef.current?.startTimer();

      const promise = dispatch(
        getStatusDiiaSignatureProcessingPersonal({
          token,
          requestId,
          applicationId,
        })
      );

      const response = await promise;
      if ((response as { error: SerializedError }).error) return;

      if ((response.payload as { status: string })?.status === 'notUsed') {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }
      if (isLoading) return;
      if ((response.payload as { status: string })?.status === 'processing') {
        const { payload: finalStatus } = await dispatch(
          getFinalStatusDiiaSignatureProcessingPersonal({
            token,
            requestId,
            applicationId,
          })
        );

        if ((finalStatus as { status: string }).status === 'done') {
          validateDocument();
        }
      } else {
        validateDocument();
      }
    } catch (error) {
      SignatureError(error);
    }
  };

  const handleSignatureIIT = async () => {
    if (isLoading) return;

    dispatch(
      startIITSigningController({
        path: `/personal-data/public/personal-data/applications/${applicationId}/get-hashes-to-sign`,
      })
    );
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    try {
      const res = await dispatch(
        saveSigningControllerFromIIT({
          fileList: signFiles,
          path: `/personal-data/public/personal-data/applications/${applicationId}/save-signed-documents`,
        })
      );

      if (res) {
        validateDocument();
      } else {
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    } catch (error) {
      dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
    } finally {
      resetIITLib();
    }
  };

  const handleSignatureSuccess = (successMessage: string): void => {
    onSignatureSuccess(successMessage);
  };

  const handleQrTimeout = (): void => {
    ignoreErrorRef.current = true;
    onQrTimeout();
  };

  const onDiiaClick = (): void => {
    ignoreErrorRef.current = false;
    handleSignatureDiia();
  };

  const handlePreview = async () => {
    await dispatch(
      previewDocumentSubmit({
        applicationId: applicationId as string,
        idDoc: applicationDocumentId,
      })
    );
  };

  return (
    <>
      <ElectronicSignature
        isModalOpen={isModalOpen}
        handleCloseModal={closeModal}
        handleSignatureDiia={onDiiaClick}
        handleSignatureIIT={handleSignatureIIT}
        onIITSign={onIITSign}
        handleTimeUp={handleQrTimeout}
        handlePreview={handlePreview}
        deepLink={deepLink}
        documents={documents}
        ref={timerRef}
        isDiiaSignatureDisabled={false}
        isIITSignatureDisabled={false}
      />

      {isLoading && <FullPageLoader />}
      {!!error && <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />}
    </>
  );
};
